import { AntDesign, Ionicons, MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import { Box, Button, HStack, Icon, IconButton, Image, Pressable, Spinner, Text, useTheme, VStack } from "native-base";
import React from "react";
import { useTranslation } from "react-i18next";
import { Text as RNText, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";

import { isDesktopScreen, Scale } from "../constants";
import { HORMONE_HEX_COLOR } from "../constants/theme";
import { findFoodBySuggestedServingId, findMatchingSuggestedServing } from "../helpers/diaryHelpers";
import { FeatureFlag, isFeatureFlagActive } from "../helpers/featureFlags";
import { getServingDescriptionText } from "../helpers/foodHelpers";
import {
  formatMealType,
  formatNumberAsWholeNumber,
  getRecipeMealImage,
  getSimpleRecipeImage,
} from "../helpers/generalHelpers";
import { getTotalRecipeTime } from "../helpers/simpleRecipeHelpers";
import { getTestIDForRecipeMeal, getTestIDForSimpleRecipeMeal } from "../helpers/testHelpers";
import {
  doesOrganisationHaveManagedPlanning,
  getOrganisation,
  shouldShowHormoneIndicator,
} from "../helpers/userHelpers";
import styles from "../screens/DiaryViewScreenStyles";
import type {
  CalendarItemContentTypeEnum,
  CalendarItemStatusEnum,
  Meal,
  MealMomentEnum,
  QuickAddMeal,
  RecipeMeal,
  SimpleRecipeMeal,
  SingleFoodMeal,
} from "../services/backendTypes";
import { FoodById, foodSelector } from "../slices/foodSlice";
import { userSelector, viewAsUserSelector } from "../slices/userSlice";

type CalendarItemComponentBaseProps = {
  onPress: () => void;
  onLongPress: () => void;
  // NOTE: This is kept because it may be used in the near future
  // eslint-disable-next-line react-redux/no-unused-prop-types
  testID?: string;
  onPressCalendarItemPlannedStatus?: () => void;
  editable?: boolean;
};

interface CalendarItemComponentProps extends CalendarItemComponentBaseProps {
  meal: Meal;
  status: CalendarItemStatusEnum;
  onSwap?: () => Promise<void>;
  contentType: CalendarItemContentTypeEnum;
  isLoading: boolean;
  editable?: boolean;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

interface SingleFoodOrQuickAddMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  title: string;
  description: string;
}

interface RecipeMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  recipeMeal: RecipeMeal;
  isLoading: boolean;
  onSwap?: () => void;
  onPressCalendarItemPlannedStatus: () => void;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

interface SimpleRecipeMealPlannerItemProps extends CalendarItemComponentBaseProps {
  isEaten: boolean;
  simpleRecipeMeal: SimpleRecipeMeal;
  isLoading: boolean;
  onSwap?: () => void;
  onPressCalendarItemPlannedStatus: () => void;
  onListAlternativesPress: () => void;
  mealMoment?: MealMomentEnum;
  onPressCopyMeal?: () => void;
}

const CheckboxComponent = ({
  onPress,
  checked,
  testID,
}: {
  onPress: () => void;
  checked: boolean;
  testID?: string;
}): JSX.Element => {
  const theme = useTheme();

  const [submitted, setSubmitted] = React.useState<boolean>(false);

  const onPressCheckbox = (): void => {
    setSubmitted(true);
    onPress();

    setTimeout(() => {
      setSubmitted(false);
    }, 750);
  };

  return (
    // NOTE: The Checkbox does not update when the checked prop changes
    // (probably something to do with it being a controlled form component).
    // As such, we wrap it in a View for use in the tests
    <>
      {!submitted ? (
        <TouchableOpacity onPress={onPressCheckbox} testID={testID}>
          <MaterialCommunityIcons
            name={checked ? "checkbox-marked-circle" : "checkbox-blank-circle-outline"}
            size={36}
            color={checked ? theme.colors.primary["600"] : theme.colors.gray["700"]}
          />
        </TouchableOpacity>
      ) : (
        <Spinner size={36} />
      )}
    </>
  );
};

const RecipeMealPlannerItem = ({
  isEaten,
  recipeMeal,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  isLoading = false,
  editable = true,
  mealMoment,
  onPressCopyMeal,
}: RecipeMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useSelector(userSelector);
  const shouldHideMacros = isFeatureFlagActive(user, FeatureFlag.FF_DONT_SHOW_MACROS);
  const shouldShowKcal = !isFeatureFlagActive(user, FeatureFlag.FF_RECIPE_INSPIRATION);

  const showHormoneIndicator = shouldShowHormoneIndicator(user, recipeMeal.recipe_template.tags);

  return (
    <Box borderRadius={theme.borderRadii.card.default} overflow="hidden" bg="white" mx={0} shadow="10">
      <Pressable onPress={onPress} onLongPress={onLongPress} testID={getTestIDForRecipeMeal(recipeMeal)}>
        <VStack>
          {/* Image Container - Top with padding */}
          <Box p={3}>
            <Box
              position="relative"
              width="100%"
              height={180}
              borderRadius={theme.borderRadii.card.default}
              overflow="hidden"
            >
              <Image
                source={getRecipeMealImage(recipeMeal).source}
                resizeMode="cover"
                alt={"Recipe img"}
                width="100%"
                height="100%"
                opacity={isEaten ? 0.6 : 1}
              />

              {/* Hormone indicator */}
              {showHormoneIndicator ? (
                <Box
                  position="absolute"
                  top={2}
                  right={2}
                  bg="white"
                  borderRadius={theme.borderRadii.badge.pill}
                  p={1.5}
                  shadow={2}
                  borderWidth={1.5}
                  borderColor={HORMONE_HEX_COLOR}
                  testID="hormoneIndicator"
                >
                  <Icon as={Ionicons} name="female" size="xs" color={HORMONE_HEX_COLOR} />
                </Box>
              ) : null}

              {/* Centered Meal Type Text */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                justifyContent="center"
                alignItems="center"
                pointerEvents="none"
              >
                <RNText
                  style={{
                    fontSize: 30,
                    fontFamily: theme.fonts.serif,
                    fontStyle: "italic",
                    color: "white",
                    fontWeight: "400",
                    textShadowColor: "rgba(0, 0, 0, 0.5)",
                    textShadowOffset: { width: 1, height: 1 },
                    textShadowRadius: 3,
                  }}
                >
                  {mealMoment ? formatMealType(mealMoment, t) : ""}
                </RNText>
              </Box>

              {/* Loading spinner overlay */}
              {isLoading ? (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  justifyContent="center"
                  alignItems="center"
                  bg="rgba(255, 255, 255, 0.8)"
                >
                  <Spinner size="lg" color="primary.500" />
                </Box>
              ) : null}
            </Box>
          </Box>

          {/* Content Container - Bottom */}
          <VStack px={3} pb={3} space={1}>
            <Text fontSize="lg" fontWeight="700" color="gray.900" strikeThrough={isEaten} numberOfLines={2}>
              {recipeMeal.recipe_template.name}
            </Text>

            <HStack alignItems="center" space={1}>
              <Text fontSize="md" color="gray.500" fontWeight="500" strikeThrough={isEaten}>
                {formatMealType(mealMoment || "BREAKFAST", t)}, {recipeMeal.recipe_template.preparation_time_min} mins
              </Text>
            </HStack>

            {shouldShowKcal && !shouldHideMacros ? (
              <HStack alignItems="center" space={1} mt={1}>
                <Text fontSize="md" color="gray.600" fontWeight="600" strikeThrough={isEaten}>
                  {formatNumberAsWholeNumber(recipeMeal.kcal)} {t("general.kcal")}
                </Text>
              </HStack>
            ) : null}

            {/* Action Buttons */}
            <HStack space={4} mt={4} justifyContent="flex-end">
              <IconButton
                onPress={onLongPress}
                icon={<Icon as={MaterialIcons} name="delete" size="md" color="white" />}
                testID={`delete-recipeMeal-${recipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                bg="primary.50"
                _icon={{
                  color: "white",
                }}
                _pressed={{
                  bg: "primary.50",
                }}
                size="md"
                p={2}
              />

              {onPressCopyMeal ? (
                <IconButton
                  onPress={onPressCopyMeal}
                  icon={<Icon as={MaterialCommunityIcons} name="content-copy" size="md" color="white" />}
                  testID={`copy-recipeMeal-${recipeMeal.id}`}
                  variant="ghost"
                  borderRadius="full"
                  bg="primary.600"
                  _icon={{
                    color: "white",
                  }}
                  _pressed={{
                    bg: "primary.700",
                  }}
                  size="md"
                  p={2}
                />
              ) : null}

              <IconButton
                onPress={onListAlternativesPress}
                icon={<Icon as={AntDesign} name="sync" size="md" color="white" />}
                testID={`swap-recipeMeal-${recipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                bg="primary.50"
                _icon={{
                  color: "white",
                }}
                _pressed={{
                  bg: "primary.50",
                }}
                size="md"
                p={2}
              />
            </HStack>
          </VStack>
        </VStack>
      </Pressable>
    </Box>
  );
};

const SimpleRecipeMealPlannerItem = ({
  isEaten,
  simpleRecipeMeal,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  isLoading = false,
  editable = true,
  mealMoment,
  onPressCopyMeal,
}: SimpleRecipeMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <VStack space={1}>
      {/* Meal Type Header */}
      <Text fontSize={12} fontWeight="light" color="#5A5A5A" letterSpacing={1.4} textTransform="uppercase">
        {mealMoment ? formatMealType(mealMoment, t) : ""}
      </Text>

      {/* Container with relative positioning for overflow effect */}
      <Pressable onPress={onPress} onLongPress={onLongPress} testID={getTestIDForSimpleRecipeMeal(simpleRecipeMeal)}>
        <Box position="relative">
          {/* Card Background - positioned behind content, inset from edges */}
          <Box
            position="absolute"
            top={0}
            left={1.5}
            right={1.5}
            bottom={0}
            bg="#FAF4EC"
            borderRadius={theme.borderRadii.card.default}
          />

          {/* Content Container */}
          <VStack>
            {/* Recipe Image - Full width, extends beyond card */}
            <Box
              height={216}
              borderRadius={theme.borderRadii.card.default}
              overflow="hidden"
              style={{
                shadowColor: "#000000",
                shadowOffset: {
                  width: 0,
                  height: 6,
                },
                shadowOpacity: 0.5,
                shadowRadius: 25,
                elevation: 15,
              }}
            >
              <Image
                source={getSimpleRecipeImage(simpleRecipeMeal).source}
                resizeMode="cover"
                alt={simpleRecipeMeal.simple_recipe.name}
                width="100%"
                height="100%"
                opacity={isEaten ? 0.6 : 1}
              />
            </Box>

            {/* Content below image - with margins to align with card */}
            <VStack px={4} py={3} space={1}>
              {/* Recipe Title */}
              <Text
                fontSize={18}
                fontFamily="PlayfairDisplay_700Bold"
                fontWeight="700"
                color="function.50"
                lineHeight={24}
                strikeThrough={isEaten}
              >
                {simpleRecipeMeal.simple_recipe.name}
              </Text>

              {/* Recipe Time */}
              <Text
                fontSize={14}
                fontFamily={theme.fonts.body}
                fontWeight="400"
                color="function.50"
                lineHeight={16}
                strikeThrough={isEaten}
              >
                {getTotalRecipeTime(simpleRecipeMeal.simple_recipe)} mins
              </Text>
            </VStack>

            {/* Action Icons Row */}
            <HStack justifyContent="flex-end" alignItems="center" px={4} pb={3} space={2}>
              <IconButton
                onPress={onListAlternativesPress}
                icon={<Icon as={AntDesign} name="sync" size="md" color="function.50" />}
                testID={`swap-simpleRecipeMeal-${simpleRecipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                _pressed={{
                  bg: "function.10",
                }}
                size="sm"
                p={2}
              />

              {onPressCopyMeal ? (
                <IconButton
                  onPress={onPressCopyMeal}
                  icon={<Icon as={MaterialCommunityIcons} name="content-copy" size="md" color="function.50" />}
                  testID={`copy-simpleRecipeMeal-${simpleRecipeMeal.id}`}
                  variant="ghost"
                  borderRadius="full"
                  _pressed={{
                    bg: "function.10",
                  }}
                  size="sm"
                  p={2}
                />
              ) : null}

              <IconButton
                onPress={onLongPress}
                icon={<Icon as={MaterialIcons} name="delete" size="md" color="function.50" />}
                testID={`delete-simpleRecipeMeal-${simpleRecipeMeal.id}`}
                variant="ghost"
                borderRadius="full"
                _pressed={{
                  bg: "function.10",
                }}
                size="sm"
                p={2}
              />
            </HStack>
          </VStack>

          {/* Loading spinner overlay */}
          {isLoading ? (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              justifyContent="center"
              alignItems="center"
              bg="rgba(255, 255, 255, 0.8)"
            >
              <Spinner size="lg" color="primary.500" />
            </Box>
          ) : null}
        </Box>
      </Pressable>
    </VStack>
  );
};

const SingleFoodOrQuickAddMealPlannerItem = ({
  isEaten,
  title,
  description,
  onLongPress,
  onPress,
  onPressCalendarItemPlannedStatus,
  testID,
  editable = true,
}: SingleFoodOrQuickAddMealPlannerItemProps): JSX.Element => {
  const theme = useTheme();
  return (
    <View
      style={{ flexDirection: "row", alignItems: "center", marginVertical: 8, paddingHorizontal: 4, marginTop: 32 }}
    >
      <Pressable
        onPress={onPress}
        onLongPress={onLongPress}
        style={{ flex: 1, flexDirection: "row", alignItems: "center" }}
        testID={testID}
        accessibilityRole="button"
      >
        <Text
          style={{
            fontWeight: "500",
            color: isEaten ? theme.colors.primary["300"] : theme.colors.gray["900"],
            textDecorationLine: isEaten ? "line-through" : "none",
            flex: 2,
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            fontWeight: "500",
            color: isEaten ? theme.colors.primary["300"] : theme.colors.gray["900"],
            textDecorationLine: isEaten ? "line-through" : "none",
            flex: 1,
          }}
        >
          {description}
        </Text>
      </Pressable>
      {editable ? (
        <View style={{ marginHorizontal: 10 }}>
          <CheckboxComponent
            onPress={() => onPressCalendarItemPlannedStatus && onPressCalendarItemPlannedStatus()}
            checked={isEaten}
            testID={`calendarItem-${isEaten ? "EATEN" : "PLANNED"}-singleFoodMealOrQuickAddMeal-NO_ID_PROVIDED`}
          />
        </View>
      ) : null}

      <IconButton
        icon={<Icon as={MaterialIcons} color={"primary.600"} name="delete" />}
        onPress={onLongPress}
        accessibilityLabel="Delete item"
        style={{ marginHorizontal: 10 }}
      />
    </View>
  );
};

const CalendarItemComponent = ({
  meal,
  status,
  contentType,
  onPress,
  onSwap,
  onLongPress,
  onPressCalendarItemPlannedStatus,
  onListAlternativesPress,
  editable = true,
  isLoading = false,
  mealMoment,
  onPressCopyMeal,
}: CalendarItemComponentProps): JSX.Element | null => {
  const { t } = useTranslation();
  const foodsById: FoodById = useSelector(foodSelector);
  const checkIsEatenStatus = (statusValue: CalendarItemStatusEnum): boolean => statusValue === "EATEN";

  const createSingleFoodMealPlannerItem = (): JSX.Element | null => {
    const singleFoodMeal: SingleFoodMeal = meal as SingleFoodMeal;
    let suggestedServing = singleFoodMeal.ingredient.suggested_serving;
    let foodName = singleFoodMeal.ingredient.name;

    if (typeof suggestedServing === "number") {
      const suggestedServingId = suggestedServing;
      // NOTE: Food is sometimes undefined when diaryscreen loads
      const food = findFoodBySuggestedServingId(suggestedServingId, foodsById);
      if (food) {
        foodName = food.name;
        suggestedServing = findMatchingSuggestedServing(food, suggestedServingId);
      }
    }

    if (suggestedServing) {
      const { nonEditableDescription } = getServingDescriptionText(suggestedServing, singleFoodMeal.ingredient);

      return (
        <SingleFoodOrQuickAddMealPlannerItem
          isEaten={checkIsEatenStatus(status)}
          title={foodName}
          description={nonEditableDescription}
          onPress={onPress}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          testID={`planned-singleFoodMeal-${singleFoodMeal.id || "MISSING_ID"}`}
          editable={editable}
        />
      );
    }
    return null;
  };

  const createQuickAddMealPlannerItem = (): JSX.Element | null => {
    const quickAddMeal: QuickAddMeal = meal as QuickAddMeal;

    return (
      <SingleFoodOrQuickAddMealPlannerItem
        isEaten={checkIsEatenStatus(status)}
        title={t("planner.item.quick_calories_title")}
        description={`${quickAddMeal.kcal} ${t("general.kcal")}`}
        onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
        onPress={onPress}
        onLongPress={onLongPress}
        testID={`planned-quickAddMeal-${quickAddMeal.id}`}
        editable={editable}
      />
    );
  };

  switch (contentType) {
    case "singlefoodmeal":
      return createSingleFoodMealPlannerItem();
    case "recipemeal":
      // eslint-disable-next-line no-case-declarations
      const recipeMeal: RecipeMeal = meal as RecipeMeal;

      if (!onPressCalendarItemPlannedStatus) {
        throw new Error("onPressCalendarItemPlannedStatus is required for RecipeMeal CalendarItem");
      }

      return (
        <RecipeMealPlannerItem
          recipeMeal={recipeMeal}
          isEaten={checkIsEatenStatus(status)}
          onPress={onPress}
          onSwap={onSwap}
          isLoading={isLoading}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          onListAlternativesPress={onListAlternativesPress}
          editable={editable}
          mealMoment={mealMoment}
          onPressCopyMeal={onPressCopyMeal}
        />
      );
    case "simplerecipemeal":
      // eslint-disable-next-line no-case-declarations
      const simpleRecipeMeal: SimpleRecipeMeal = meal as SimpleRecipeMeal;

      if (!onPressCalendarItemPlannedStatus) {
        throw new Error("onPressCalendarItemPlannedStatus is required for SimpleRecipeMeal CalendarItem");
      }

      return (
        <SimpleRecipeMealPlannerItem
          simpleRecipeMeal={simpleRecipeMeal}
          isEaten={checkIsEatenStatus(status)}
          onPress={onPress}
          onSwap={onSwap}
          isLoading={isLoading}
          onLongPress={onLongPress}
          onPressCalendarItemPlannedStatus={onPressCalendarItemPlannedStatus}
          onListAlternativesPress={onListAlternativesPress}
          editable={editable}
          mealMoment={mealMoment}
          onPressCopyMeal={onPressCopyMeal}
        />
      );
    case "quickaddmeal":
      return createQuickAddMealPlannerItem();
    default:
      return null;
  }
};

export default CalendarItemComponent;
